import { useAuth0 } from '@auth0/auth0-react';
import { getAuth } from 'firebase/auth';
import { useCallback } from 'react';
import { firebaseApp } from 'firebaseConfig';
import {
  Auth0TokenError,
  Auth0TokenResponse,
  REFRESH_TOKEN_LIFETIME_MS,
  installAuth0Session,
  readSdkRefreshToken,
  redeemRefreshToken,
} from 'utils/helpers/auth0Session.helpers';
import { wrap } from 'utils/helpers/pinCrypto.helpers';
import { isCachedRtExpired, setCachedPinKey, setPinLockout } from 'utils/helpers/pinKeyCache.helpers';
import { saveSessionWrapKey } from 'utils/helpers/pinSessionKey.helpers';
import { PinAttemptFailure, toPinAttemptFailure, unwrapCachedRefreshToken } from 'utils/helpers/pinUnlock.helpers';
import { usePinOrgId } from './usePinOrgId';

export type PinLoginResult = { status: 'success' } | { status: 'password_required' } | PinAttemptFailure;

// Logs a user in with their PIN: unwrap the cached refresh token, redeem it with Auth0, re-wrap the
// rotated token under the same key, then reload as that user. The re-wrapped pin.key is queued in the
// local cache and written to the keystore after login (see useFlushPendingPinKey).
export const useLoginWithPin = () => {
  const { loginWithPopup, getIdTokenClaims } = useAuth0();
  const orgId = usePinOrgId();

  // Expired or already-rotated refresh token: step up with the password in a popup (a redirect would
  // lose the PIN-derived key held in memory), and hand back the fresh refresh token.
  const stepUpWithPassword = useCallback(
    async (email: string, organization: string): Promise<string | undefined> => {
      await loginWithPopup({ authorizationParams: { login_hint: email, prompt: 'login', organization } });

      const claims = await getIdTokenClaims();
      if (claims?.email?.toLowerCase() !== email.toLowerCase()) throw new Error('Signed in as a different user');

      return readSdkRefreshToken();
    },
    [loginWithPopup, getIdTokenClaims],
  );

  return useCallback(
    async (email: string, pin: string): Promise<PinLoginResult> => {
      // no org means nobody has logged in here with an org to remember, so there is nothing cached
      if (!orgId) return { status: 'password_required' };

      // the pin.key metadata says the refresh token is already dead: skip the PIN attempt entirely
      if (isCachedRtExpired(orgId, email)) return { status: 'password_required' };

      let unwrapped: Awaited<ReturnType<typeof unwrapCachedRefreshToken>>;
      try {
        unwrapped = await unwrapCachedRefreshToken(orgId, email, pin);
      } catch (error) {
        const failure = toPinAttemptFailure(error);
        if (failure) {
          if (failure.status === 'locked_out') setPinLockout(orgId, email, failure.retryAfterSeconds);
          return failure;
        }
        throw error;
      }

      const { entry, key } = unwrapped;
      let tokens: Auth0TokenResponse | undefined;
      let nextRefreshToken: string | undefined;
      let expiryEpochMs = entry.expiryEpochMs;

      try {
        tokens = await redeemRefreshToken(unwrapped.refreshToken);
        nextRefreshToken = tokens.refresh_token ?? unwrapped.refreshToken;
      } catch (error) {
        if (!(error instanceof Auth0TokenError) || error.error !== 'invalid_grant') throw error;

        nextRefreshToken = await stepUpWithPassword(email, orgId);
        expiryEpochMs = Date.now() + REFRESH_TOKEN_LIFETIME_MS;
      }
      unwrapped.refreshToken = '';

      if (!nextRefreshToken) throw new Error('Auth0 did not return a refresh token');
      // a non-rotating redemption returns no refresh token; the session still needs it
      if (tokens) tokens.refresh_token = nextRefreshToken;

      const { iv, ciphertext } = await wrap(key, nextRefreshToken);
      await saveSessionWrapKey(orgId, email, key, entry.tokenStore.salt, nextRefreshToken);
      nextRefreshToken = undefined;

      setCachedPinKey(orgId, email, {
        ...entry,
        expiryEpochMs,
        tokenStore: { salt: entry.tokenStore.salt, iv, ciphertext },
        pendingUpload: true,
      });

      // Firebase keeps the previous user's identity; drop it so the reload signs in as this user
      await getAuth(firebaseApp).signOut();
      if (tokens) installAuth0Session(tokens);
      window.location.assign('/');

      return { status: 'success' };
    },
    [orgId, stepUpWithPassword],
  );
};
